import { useAssetClassColors } from '@containers/AssetClassColors/useAssetClassColors';
import { AssetClassLevel } from '@generated/graphql';
import type {
  AssetClassBranch,
  PortfolioTemplate,
} from '@interfaces/financialProduct';
import { ASSET_CLASS_FIELD_BY_LEVEL } from '@interfaces/financialProduct';
import { Card, CardContent } from '@ovComponents/2-component/card/card';
import {
  type AssetClassColor,
  type AssetClassColorSource,
  assetClassLightColor,
  assetClassSolidColor,
  useAssetClassColorRamp,
} from '@ovComponents/2-component/pieChart/getChartColors';
import { PieChart } from '@ovComponents/2-component/pieChart/pieChart';
import { Box, Skeleton, Typography, useTheme } from 'hephaestus/web';
import { isEmpty, isUndefined } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../../hooks/useLocalization';
import type { Security } from '../securitiesSelectionTable/securitiesSelectionTable';
import { SelectionChip } from '../selectionChip/selectionChip';

type AssetClassGroup = {
  totalPercentage: number;
  name: string;
  /** Palette colour for the asset class itself, the same in every chart. */
  color: AssetClassColor;
  templateTargetPercentage: number;
};

type PieChartSecuritiesProps = {
  securities: Security[];
  variant?: 'elevated' | 'outlined' | 'none';
  modelPortfolioName?: string;
  portfolioTemplate?: PortfolioTemplate;
  testId?: string;
  assetClassLevel?: AssetClassLevel | string;
  /**
   * Asset class models render from template branches instead of securities, so
   * they take the solid ramp. Standard models reach the same branch only before
   * securities exist, where the light ramp marks targets as pending.
   */
  isAssetClassStrategy?: boolean;
};

type PieChartData = {
  pieChartData: number[];
  pieChartColors: string[];
};

const DEFAULT_BORDER_RADIUS = '4px';
const DEFAULT_COLOR = '#FFFFFF';
const DEFAULT_TEXT_COLOR = '#17181C';
const ensureString = (value: unknown, defaultValue: string): string => {
  if (typeof value === 'string' && value !== '') {
    return value;
  }
  return defaultValue;
};

const formatPercentage = (value: number): string =>
  `${Number(value).toFixed(2)}%`;

const AssetClassLegendItem = ({
  dataTestId,
  item,
  isActive,
  isLast,
  color,
  surfaceContainerColor,
  surfaceColor,
  borderRadius,
  onMouseEnter,
  onMouseLeave,
}: {
  dataTestId: string;
  item: AssetClassGroup;
  isActive: boolean;
  isLast: boolean;
  color: string;
  surfaceContainerColor: string;
  surfaceColor: string;
  borderRadius: string;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}) => (
  <Box
    data-testid={dataTestId}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'left',

      borderBottom:
        !isActive && !isLast
          ? `2px solid ${surfaceContainerColor}`
          : `2px solid ${surfaceColor}`,

      py: 0.5,
      px: 1,
      backgroundColor: isActive ? surfaceContainerColor : 'none',
      borderRadius: isActive ? borderRadius : '0px',
    }}
  >
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'start',
        alignItems: 'center',
      }}
    >
      <Box
        sx={{
          borderRadius: '10px',
          height: '8px',
          width: '8px',
          backgroundColor: color,
          mr: 1,
        }}
      />
      <Typography
        variant='bodyLarge'
        data-testid={`${dataTestId}-legend-item-name`}
        sx={{ fontSize: '0.75rem' }}
      >
        {item.templateTargetPercentage &&
        item.totalPercentage !== item.templateTargetPercentage
          ? `${item.name} (${item.templateTargetPercentage}%)`
          : item.name}
      </Typography>
    </Box>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
      }}
    >
      <Typography
        variant='bodyLarge'
        data-testid={`${dataTestId}-legend-item-percentage`}
        sx={{
          fontSize: '0.75rem',
          color:
            item.templateTargetPercentage &&
            item.totalPercentage !== item.templateTargetPercentage
              ? '#D32F2F'
              : DEFAULT_TEXT_COLOR,
        }}
      >
        {formatPercentage(item.totalPercentage || 0)}
      </Typography>
    </Box>
  </Box>
);

export const PieChartSecurities = ({
  securities = [],
  variant = 'none',
  modelPortfolioName = '',
  portfolioTemplate,
  testId = 'pie-chart-securities',
  assetClassLevel,
  isAssetClassStrategy = false,
}: PieChartSecuritiesProps) => {
  const { translateBackendString } = useLocalization();
  const [activeItem, setActiveItem] = useState<AssetClassGroup | null>(null);
  const theme = useTheme();
  const { t } = useTranslation([
    'shared',
    'modelPortfolios',
    'client',
    'buildModels',
  ]);

  const assetClassColors = useAssetClassColorRamp();
  const templateSliceColor = useCallback(
    (color: AssetClassColor) =>
      isAssetClassStrategy
        ? assetClassSolidColor(assetClassColors, color)
        : assetClassLightColor(assetClassColors, color),
    [assetClassColors, isAssetClassStrategy],
  );

  const resolvedAssetClassField = useMemo(
    () =>
      ASSET_CLASS_FIELD_BY_LEVEL[
        (assetClassLevel as AssetClassLevel) ?? AssetClassLevel.Primary
      ] ?? ASSET_CLASS_FIELD_BY_LEVEL[AssetClassLevel.Primary],
    [assetClassLevel],
  );

  const securitiesDataState = useMemo(() => {
    const isDummyData =
      securities.length === 1 &&
      !securities[0].financialProduct.primaryAssetClass?.translatedName;
    const hasData = !isEmpty(securities) && !isDummyData;
    return { isDummyData, hasData };
  }, [securities]);

  /**
   * The same set the table beside this chart derives, so both land on the same
   * colours. Has to turn on the same `hasData` the slices do: a lone
   * placeholder row is not painted, and the template branches drawn in its
   * place are the ones needing distinct colours.
   */
  const assetClassesInView = useMemo(
    () =>
      securitiesDataState.hasData
        ? securities.map(
            (security) =>
              security.financialProduct[
                resolvedAssetClassField
              ] as AssetClassColorSource | null,
          )
        : (portfolioTemplate?.assetClassBranches ?? []).map(
            (branch) => branch.assetClass as AssetClassColorSource | null,
          ),
    [
      securities,
      securitiesDataState.hasData,
      resolvedAssetClassField,
      portfolioTemplate,
    ],
  );

  const { resolve: resolveAssetClassColor, loading: assetClassColorsLoading } =
    useAssetClassColors(assetClassesInView);

  const portfolioTemplateAssetClassBranches = useMemo(() => {
    if (!portfolioTemplate?.assetClassBranches) {
      return [];
    }

    const resolveBranchName = (branch: AssetClassBranch): string =>
      translateBackendString(branch.assetClass.translatedName) ||
      branch.assetClass.name ||
      '';

    return portfolioTemplate.assetClassBranches.map((branch) => ({
      name: resolveBranchName(branch),
      color: resolveAssetClassColor(branch.assetClass),
      templateTargetPercentage: branch.targetPercentage || 0,
    }));
  }, [portfolioTemplate, resolveAssetClassColor]);

  const assetClassData = useMemo(() => {
    const getAssetClassSource = (security: Security) =>
      security.financialProduct[resolvedAssetClassField] as
        | (AssetClassColorSource & { translatedName?: Record<string, string> })
        | undefined;

    const getAssetClassName = (security: Security): string => {
      const assetClassObj = getAssetClassSource(security);
      return (
        translateBackendString(assetClassObj?.translatedName) ||
        assetClassObj?.name ||
        t('buildModels:other')
      );
    };

    const assetClassGroups = securities.reduce(
      (acc: Record<string, AssetClassGroup>, security: Security) => {
        const assetClass = getAssetClassName(security);
        const templateBranch = portfolioTemplateAssetClassBranches.find(
          (b) => b.name === assetClass,
        );

        if (acc[assetClass]) {
          acc[assetClass].totalPercentage += security.percentage || 0;
        } else {
          acc[assetClass] = {
            totalPercentage: security.percentage || 0,
            name: assetClass,
            color: resolveAssetClassColor(getAssetClassSource(security)),
            templateTargetPercentage:
              templateBranch?.templateTargetPercentage || 0,
          };
        }
        return acc;
      },
      {},
    );

    portfolioTemplateAssetClassBranches.forEach((branch) => {
      if (branch.name && !assetClassGroups[branch.name]) {
        assetClassGroups[branch.name] = {
          // When there is no actual securities data, use the template target as
          // the displayed value so the legend shows the intended allocation.
          totalPercentage: securitiesDataState.hasData
            ? 0
            : branch.templateTargetPercentage,
          name: branch.name,
          color: branch.color,
          templateTargetPercentage: branch.templateTargetPercentage,
        };
      }
    });

    return Object.values(assetClassGroups).sort(
      (a, b) => b.totalPercentage - a.totalPercentage,
    );
  }, [
    securities,
    portfolioTemplateAssetClassBranches,
    resolveAssetClassColor,
    resolvedAssetClassField,
    securitiesDataState,
    t,
  ]);

  const pieChartDataAndColors = useMemo((): PieChartData => {
    if (securitiesDataState.hasData) {
      const totalPercentage = assetClassData.reduce(
        (acc: number, x) => acc + Number(x.totalPercentage),
        0,
      );
      const remainingPercentage = Math.max(0, 100 - totalPercentage);

      return {
        pieChartData: [
          ...assetClassData.map((x) => Number(x.totalPercentage)),
          remainingPercentage,
        ],
        pieChartColors: [
          ...assetClassData.map((x) =>
            assetClassSolidColor(assetClassColors, x.color),
          ),
          ensureString(theme.color.surfaceContainer, DEFAULT_COLOR),
        ],
      };
    }

    if (portfolioTemplateAssetClassBranches.length) {
      const templateData = assetClassData.filter(
        (x) => x.templateTargetPercentage > 0,
      );
      const totalPercentage = templateData.reduce(
        (acc: number, x) => acc + Number(x.totalPercentage),
        0,
      );
      const remainingPercentage = Math.max(0, 100 - totalPercentage);

      return {
        pieChartData: [
          ...templateData.map((x) => Number(x.totalPercentage)),
          remainingPercentage,
        ],
        pieChartColors: [
          ...templateData.map((x) => templateSliceColor(x.color)),
          ensureString(theme.color.surfaceContainer, DEFAULT_COLOR),
        ],
      };
    }

    return {
      pieChartData: [100],
      pieChartColors: [
        ensureString(theme.color.surfaceContainer, DEFAULT_COLOR),
      ],
    };
  }, [
    assetClassData,
    assetClassColors,
    templateSliceColor,
    theme.color.surfaceContainer,
    securitiesDataState.hasData,
    portfolioTemplateAssetClassBranches,
  ]);

  const renderCenterContent = () => {
    if (
      !securitiesDataState.hasData &&
      !portfolioTemplateAssetClassBranches.length
    ) {
      return (
        <>
          <Typography
            variant='bodyLarge'
            data-testid={`${testId}-pie-chart-no-data`}
            sx={{
              color: theme.color.onSurfaceVariant,
              fontSize: '0.875rem',
            }}
          >
            {modelPortfolioName}
          </Typography>
          <Typography
            variant='bodyLarge'
            data-testid={`${testId}-pie-chart-no-data-percentage`}
            sx={{ fontSize: '0.875rem', fontWeight: 500 }}
          >
            0.00%
          </Typography>
        </>
      );
    }

    if (activeItem) {
      return (
        <>
          <Typography
            variant='bodyLarge'
            data-testid={`${testId}-pie-chart-active-item`}
            sx={{
              color: theme.color.onSurfaceVariant,
              fontSize: '0.875rem',
            }}
          >
            {activeItem.templateTargetPercentage &&
            activeItem.totalPercentage !== activeItem.templateTargetPercentage
              ? `${activeItem.name} (${activeItem.templateTargetPercentage}%)`
              : activeItem.name}
          </Typography>
          <Typography
            variant='bodyLarge'
            data-testid={`${testId}-pie-chart-active-item-percentage`}
            sx={{ fontSize: '0.875rem', fontWeight: 500 }}
          >
            {formatPercentage(activeItem.totalPercentage)}
          </Typography>
        </>
      );
    }

    return (
      <>
        <Typography
          variant='bodyLarge'
          data-testid={`${testId}-pie-chart-no-data-name`}
          sx={{
            color: theme.color.onSurfaceVariant,
            fontSize: '0.875rem',
          }}
        >
          {modelPortfolioName}
        </Typography>
        <Typography
          variant='bodyLarge'
          data-testid={`${testId}-pie-chart-no-data-percentage`}
          sx={{ fontSize: '0.875rem', fontWeight: 500 }}
        >
          {`${assetClassData.reduce((acc: number, x) => acc + Number(x.totalPercentage), 0).toFixed(2)}%`}
        </Typography>
      </>
    );
  };

  // A donut is nothing but colour, so there is no drawing it from a registry
  // that has not arrived. The table beside it keeps rendering and falls its
  // chips back to neutral.
  if (assetClassColorsLoading) {
    return (
      <Card variant={variant}>
        <CardContent sx={{ p: '14px !important' }}>
          <Box
            data-testid={`${testId}-loading`}
            sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
          >
            <Skeleton variant='rounded' width='40%' height={32} />
            <Skeleton
              variant='circular'
              width={240}
              height={240}
              sx={{ alignSelf: 'center' }}
            />
            <Skeleton variant='rounded' width='100%' height={120} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant={variant}>
      <CardContent sx={{ p: '14px !important' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>
            <SelectionChip
              onChange={() => {}}
              value={assetClassLevel ?? AssetClassLevel.Primary}
              options={[
                {
                  value: assetClassLevel ?? AssetClassLevel.Primary,
                  label: t(
                    `modelPortfolios:${assetClassLevel ?? AssetClassLevel.Primary}`,
                  ),
                  dataTestId: `${testId}-selection-chip-asset-class-level`,
                },
              ]}
            />
          </Box>
          <Box
            sx={{
              position: 'relative',
              height: 270,
              mb: 0.5,
            }}
          >
            <PieChart
              testId={`${testId}-pie-chart`}
              datasets={[
                {
                  label: '',
                  data: pieChartDataAndColors.pieChartData,
                },
              ]}
              labels={assetClassData.map((x) => x.name)}
              onHover={(index: number | undefined) => {
                if (!isUndefined(index) && index < assetClassData.length) {
                  setActiveItem(assetClassData[index]);
                } else {
                  setActiveItem(null);
                }
              }}
              customColors={pieChartDataAndColors.pieChartColors}
              cutoutPercentage={80}
            />
            <Box
              sx={{
                textAlign: 'center',
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%,-50%)',
              }}
            >
              {renderCenterContent()}
            </Box>
          </Box>

          {securitiesDataState.hasData ||
          portfolioTemplateAssetClassBranches.length ? (
            <Box>
              {assetClassData.map((item, i) => (
                <AssetClassLegendItem
                  dataTestId={`${testId}-pie-chart-legend-item`}
                  key={item.name}
                  item={item}
                  isActive={activeItem?.name === item.name}
                  isLast={i + 1 === assetClassData.length}
                  color={
                    securitiesDataState.hasData
                      ? assetClassSolidColor(assetClassColors, item.color)
                      : templateSliceColor(item.color)
                  }
                  surfaceContainerColor={ensureString(
                    theme.color.surfaceContainer,
                    DEFAULT_COLOR,
                  )}
                  surfaceColor={ensureString(
                    theme.color.surface,
                    DEFAULT_COLOR,
                  )}
                  borderRadius={ensureString(
                    theme.borderRadius.md,
                    DEFAULT_BORDER_RADIUS,
                  )}
                  onMouseEnter={() => setActiveItem(item)}
                  onMouseLeave={() => setActiveItem(null)}
                />
              ))}
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'left',
                py: 0.5,
                px: 1,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'start',
                  alignItems: 'center',
                }}
              >
                <Box
                  sx={{
                    borderRadius: '10px',
                    height: '8px',
                    width: '8px',
                    backgroundColor: theme.color.surfaceContainer,
                    mr: 1,
                  }}
                />
                <Typography
                  variant='bodyLarge'
                  data-testid={`${testId}-pie-chart-legend-item-name`}
                  sx={{ fontSize: '0.75rem' }}
                >
                  {t('modelPortfolios:modelPortfolioTable.assetClass')}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <Typography
                  variant='bodyLarge'
                  data-testid={`${testId}-pie-chart-legend-item-percentage`}
                  sx={{ fontSize: '0.75rem', color: DEFAULT_TEXT_COLOR }}
                >
                  --%
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};
